'use client'

import { Suspense, useState, useEffect } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'
import { signIn } from 'next-auth/react'
import { Eye, EyeOff, Mail, Lock, Search, Zap, ShoppingCart, BarChart2, ShieldCheck, X, Headphones } from 'lucide-react'
import { toast } from 'sonner'
import { LoginLeftBackgroundEffects, LoginTitleGlow } from '@/components/login/login-left-background-effects'
import { PrimodropLogo } from '@/components/primodrop-logo'

const features = [
  { Icon: Search, title: 'Produtos vencedores', desc: 'Encontre os melhores produtos com alta margem de lucro.' },
  { Icon: Zap, title: 'Publicação automática', desc: 'Publique anúncios no Mercado Livre em segundos.' },
  { Icon: ShoppingCart, title: 'Gestão de pedidos', desc: 'Importe pedidos e envie para os fornecedores com um clique.' },
  { Icon: BarChart2, title: 'Relatórios inteligentes', desc: 'Acompanhe vendas, lucros e métricas em tempo real.' },
]

export default function LoginPage() {
  return (
    <Suspense fallback={<LoginPageFallback />}>
      <LoginContent />
    </Suspense>
  )
}

function LoginPageFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F9]">
      <p className="text-sm text-gray-500">Carregando...</p>
    </div>
  )
}

function LoginContent() {
  const router = useRouter()
  const searchParams = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)
  const [step, setStep] = useState<'credentials' | '2fa'>('credentials')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totp, setTotp] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [forgotModal, setForgotModal] = useState<'support' | 'request' | null>(null)
  const [forgotEmail, setForgotEmail] = useState('')
  const [forgotLoading, setForgotLoading] = useState(false)
  const [checkingForgot, setCheckingForgot] = useState(false)
  const [googleAvailable, setGoogleAvailable] = useState(false)
  const [checkingGoogle, setCheckingGoogle] = useState(true)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch('/api/auth/google/status')
        const body = await res.json()
        if (!cancelled) setGoogleAvailable(!!body.available)
      } catch {
        if (!cancelled) setGoogleAvailable(false)
      } finally {
        if (!cancelled) setCheckingGoogle(false)
      }
    })()
    return () => { cancelled = true }
  }, [])

  useEffect(() => {
    const error = searchParams.get('error')
    if (error === 'inactive') {
      toast.error('Sua conta está inativa. Entre em contato com o suporte.')
    } else if (error === 'session_revoked') {
      toast.error('Sua sessão foi encerrada. Faça login novamente.')
    } else if (error === 'google_email') {
      toast.error('Não foi possível obter o e-mail da conta Google.')
    } else if (error === 'OAuthAccountNotLinked') {
      toast.error('Este e-mail já está cadastrado. Entre com e-mail e senha.')
    } else if (error) {
      toast.error('Não foi possível entrar com Google. Tente novamente.')
    }
  }, [searchParams])

  async function handleGoogleSignIn() {
    if (!googleAvailable) {
      toast.error('Login com Google indisponível. Peça ao administrador para configurar.')
      return
    }
    await signIn('google', { callbackUrl: '/dashboard' })
  }

  async function handleForgotPasswordClick(e: React.MouseEvent) {
    e.preventDefault()
    setCheckingForgot(true)
    try {
      const res = await fetch('/api/auth/forgot-password')
      const body = await res.json()
      setForgotEmail(email)
      setForgotModal(body.smtpAvailable ? 'request' : 'support')
    } catch {
      setForgotModal('support')
    } finally {
      setCheckingForgot(false)
    }
  }

  async function submitForgotPassword(e: React.FormEvent) {
    e.preventDefault()
    setForgotLoading(true)
    try {
      const res = await fetch('/api/auth/forgot-password', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail }),
      })
      const body = await res.json()
      if (!res.ok) {
        toast.error(body.message ?? 'Não foi possível enviar o e-mail')
        return
      }
      toast.success(body.message)
      setForgotModal(null)
    } catch {
      toast.error('Erro ao solicitar redefinição de senha')
    } finally {
      setForgotLoading(false)
    }
  }

  async function completeSignIn(totpCode?: string) {
    const result = await signIn('credentials', {
      email,
      password,
      totp: totpCode ?? totp,
      redirect: false,
    })

    if (result?.error === '2FA_REQUIRED') {
      setStep('2fa')
      return
    }

    if (result?.error) {
      toast.error(step === '2fa' ? 'Código 2FA inválido' : 'Email ou senha inválidos')
      return
    }

    toast.success('Login realizado com sucesso!')
    router.push('/dashboard')
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    setIsLoading(true)
    try {
      if (step === '2fa') {
        if (!/^\d{6}$/.test(totp.trim())) {
          toast.error('Informe o código de 6 dígitos')
          return
        }
        await completeSignIn(totp.trim())
        return
      }

      const res = await fetch('/api/auth/prelogin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      })
      const body = await res.json()

      if (!res.ok) {
        toast.error(body.message ?? 'Email ou senha inválidos')
        return
      }

      if (body.requires2fa) {
        setStep('2fa')
        toast.info('Informe o código do autenticador')
        return
      }

      await completeSignIn()
    } catch {
      toast.error('Erro ao realizar login')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex flex-col lg:flex-row bg-white">
      {/* ───────────────── Left dark panel (coded, crisp) ───────────────── */}
      <div className="hidden lg:flex lg:w-1/2 bg-black flex-col justify-center px-16 py-14 relative overflow-hidden">
        <LoginLeftBackgroundEffects />
        {/* Wireframe open-box, top-right (partially off screen) */}
        <svg
          className="absolute -top-10 right-0 w-[340px] h-[340px] pointer-events-none select-none"
          viewBox="0 0 200 200"
          fill="none"
          aria-hidden
        >
          <g stroke="#ffffff" strokeWidth="1.4" opacity="0.14" strokeLinejoin="round">
            {/* box body (isometric cube) */}
            <path d="M100 55 L160 85 L160 150 L100 180 L40 150 L40 85 Z" />
            <path d="M100 55 L100 120 M100 120 L160 150 M100 120 L40 150" />
            {/* open top flaps */}
            <path d="M100 55 L60 35 L120 5 L160 85" />
            <path d="M100 55 L140 35 L80 5 L40 85" />
          </g>
        </svg>

        {/* Content */}
        <div className="relative z-10 max-w-lg">
          <LoginTitleGlow />
          {/* Logo */}
          <PrimodropLogo variant="white" height={52} href={null} />

          {/* Title */}
          <h2 className="mt-10 text-[2.5rem] font-bold text-white leading-[1.12] tracking-tight">
            A plataforma completa para voce vender sem estoque no Mercado Livre.
          </h2>

          {/* Description */}
          <p className="mt-6 text-gray-400 text-[15px] leading-relaxed max-w-md">
            Encontre produtos vencedores, publique anúncios em segundos e gerencie seus pedidos com automação e inteligência.
          </p>

          {/* Features */}
          <div className="mt-12 space-y-6">
            {features.map(({ Icon, title, desc }) => (
              <div key={title} className="flex items-start gap-4">
                <div className="w-12 h-12 bg-white/[0.03] border border-white/10 rounded-xl flex items-center justify-center shrink-0">
                  <Icon className="w-[18px] h-[18px] text-white" strokeWidth={1.6} />
                </div>
                <div className="pt-1">
                  <p className="text-white font-semibold text-[15px]">{title}</p>
                  <p className="text-gray-400 text-[13px] mt-1 leading-relaxed">{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ───────────────── Right light panel ───────────────── */}
      <div className="w-full lg:w-1/2 flex flex-col items-center justify-between bg-[#F9F9F9] py-12 px-6 sm:px-8">
        {/* Mobile logo */}
        <div className="lg:hidden w-full max-w-md flex justify-center">
          <PrimodropLogo variant="blackBg" height={40} href={null} />
        </div>

        <div className="w-full max-w-md flex-1 flex flex-col justify-center py-12">
          {/* Login card */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-[0_10px_40px_rgba(0,0,0,0.06)] p-8 sm:p-10 space-y-6">
            <div className="text-center space-y-2">
              <h1 className="text-[1.75rem] font-bold text-gray-900 leading-tight">Bem-vindo de volta!</h1>
              <p className="text-sm text-gray-500">Faça login para acessar sua conta.</p>
            </div>

            <form onSubmit={onSubmit} className="space-y-5">
              {step === 'credentials' ? (
                <>
              {/* Email */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-600">E-mail</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" strokeWidth={1.5} />
                  <input
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    required
                    className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-300 placeholder-gray-300 transition"
                  />
                </div>
              </div>

              {/* Password */}
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-600">Senha</label>
                <div className="relative">
                  <Lock className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" strokeWidth={1.5} />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Sua senha"
                    value={password}
                    onChange={e => setPassword(e.target.value)}
                    required
                    className="w-full pl-11 pr-11 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-300 placeholder-gray-300 transition"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(v => !v)}
                    className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                  >
                    {showPassword ? <EyeOff className="w-[18px] h-[18px]" strokeWidth={1.5} /> : <Eye className="w-[18px] h-[18px]" strokeWidth={1.5} />}
                  </button>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleForgotPasswordClick}
                    disabled={checkingForgot}
                    className="text-xs text-blue-600 hover:underline disabled:opacity-60"
                  >
                    {checkingForgot ? 'Carregando...' : 'Esqueceu sua senha?'}
                  </button>
                </div>
              </div>
                </>
              ) : (
                <div className="space-y-4">
                  <div className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                    <div className="flex items-center gap-2 font-medium text-gray-900">
                      <ShieldCheck className="h-4 w-4" />
                      Verificação em duas etapas
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      Abra o Google Authenticator (ou similar) e informe o código de 6 dígitos.
                    </p>
                  </div>
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-600">Código 2FA</label>
                    <input
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      placeholder="000000"
                      value={totp}
                      onChange={(e) => setTotp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                      required
                      autoFocus
                      className="w-full px-4 py-3 bg-white border border-gray-200 rounded-lg text-center text-lg tracking-[0.35em] font-semibold focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-300 transition"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => { setStep('credentials'); setTotp('') }}
                    className="text-xs text-gray-500 hover:text-gray-800"
                  >
                    Voltar para email e senha
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 bg-black text-white font-semibold rounded-lg text-sm hover:bg-gray-900 transition-colors disabled:opacity-60"
              >
                {isLoading ? 'Entrando...' : step === '2fa' ? 'Verificar código' : 'Entrar'}
              </button>
            </form>

            {/* Google */}
            {googleAvailable && (
              <>
            <div className="flex items-center gap-3">
              <div className="flex-1 h-px bg-gray-200" />
              <span className="text-xs text-gray-400">ou continue com</span>
              <div className="flex-1 h-px bg-gray-200" />
            </div>

            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={checkingGoogle}
              className="w-full flex items-center justify-center gap-2 py-3 bg-white border border-gray-200 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60"
            >
              <GoogleIcon />
              Entrar com Google
            </button>
              </>
            )}

            <p className="text-center text-sm text-gray-500 pt-1">
              Ainda não tem uma conta?{' '}
              <a href="/register" className="font-bold text-gray-900 hover:underline">
                Criar conta
              </a>
            </p>
          </div>
        </div>

        <p className="text-xs text-gray-400 text-center">
          © {new Date().getFullYear()} Primodrop. Todos os direitos reservados.
        </p>
      </div>

      {forgotModal === 'support' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md bg-white rounded-2xl border border-gray-200 shadow-xl p-6 sm:p-8 space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-gray-100 flex items-center justify-center">
                  <Headphones className="w-5 h-5 text-gray-700" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900">Recuperação indisponível</h2>
                  <p className="text-sm text-gray-500 mt-0.5">Entre em contato com o suporte</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setForgotModal(null)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-600 leading-relaxed">
              A recuperação de senha por e-mail ainda não está disponível nesta plataforma.
              Entre em contato com o suporte para redefinir sua senha.
            </p>
            <button
              type="button"
              onClick={() => setForgotModal(null)}
              className="w-full py-3 bg-black text-white font-semibold rounded-lg text-sm hover:bg-gray-900 transition-colors"
            >
              Entendi
            </button>
          </div>
        </div>
      )}

      {forgotModal === 'request' && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md bg-white rounded-2xl border border-gray-200 shadow-xl p-6 sm:p-8 space-y-5">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Esqueceu sua senha?</h2>
                <p className="text-sm text-gray-500 mt-1">
                  Informe seu e-mail e enviaremos um link para redefinir a senha.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setForgotModal(null)}
                className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={submitForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-medium text-gray-600">E-mail</label>
                <div className="relative">
                  <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-[18px] h-[18px] text-gray-400" strokeWidth={1.5} />
                  <input
                    type="email"
                    placeholder="seu@email.com"
                    value={forgotEmail}
                    onChange={e => setForgotEmail(e.target.value)}
                    required
                    autoFocus
                    className="w-full pl-11 pr-4 py-3 bg-white border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-black/10 focus:border-gray-300 placeholder-gray-300 transition"
                  />
                </div>
              </div>
              <button
                type="submit"
                disabled={forgotLoading}
                className="w-full py-3 bg-black text-white font-semibold rounded-lg text-sm hover:bg-gray-900 transition-colors disabled:opacity-60"
              >
                {forgotLoading ? 'Enviando...' : 'Enviar link de recuperação'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

function GoogleIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
      <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
      <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332C2.438 15.983 5.482 18 9 18z" fill="#34A853"/>
      <path d="M3.964 10.707c-.18-.54-.282-1.117-.282-1.707s.102-1.167.282-1.707V4.961H.957C.347 6.173 0 7.548 0 9s.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
      <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0 5.482 0 2.438 2.017.957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
    </svg>
  )
}
